import {
  JSNS,
  type JsonObject,
  type Appointment,
  type AppointmentDatum,
  type CalTzInfo,
  type ConflictInstance,
  type DateTimeInfo,
  type ExpandedInstance,
  type FreeBusyUser,
  type InviteComponent,
  type MiniCalError,
  type WorkingHoursUser,
} from '@zimbra-webif/types';
import type { ZimbraClient } from '../client';

/* ========================================================================= */
/* Shared param/response shapes                                              */
/* ========================================================================= */

/**
 * The message envelope `<m>` carried by Create/Modify/Set/Counter/Forward etc.
 * `f`=flags, `l`=target folder; `e`=recipients; `su`=subject; `inv`=invite body;
 * `mp`=note/body parts; `content`=raw RFC822/ICS. Left open because callers
 * compose the invite body (`<inv>`/`<comp>`) directly from typed pieces.
 */
export interface CalMessage {
  /** flags */
  f?: string;
  /** target folder id */
  l?: string;
  /** recipients (`<e a t/>`) */
  e?: Array<{ a: string; t?: string; p?: string; [k: string]: unknown }>;
  /** subject `<su>` */
  su?: string | { _content: string };
  /** invite `<inv>` (one or more `<comp>` + optional `<tz>`) */
  inv?: { tz?: CalTzInfo[]; comp?: InviteComponent[]; [k: string]: unknown };
  /** note / body parts */
  mp?: JsonObject[];
  /** raw content / uploaded attachment */
  content?: JsonObject;
  [k: string]: unknown;
}

/** Echo options shared by create/modify/exception. */
export interface EchoOptions {
  /** echo the resulting item back in the response */
  echo?: boolean;
  /** max body size for the echoed `<m>` */
  max?: number;
  /** return HTML body in the echo */
  html?: boolean;
  /** neuter the echoed body (strip dangerous content) */
  neuter?: boolean;
}

/** Conflict-detection + send options shared by create/modify/exception. */
export interface MutationOptions extends EchoOptions {
  /** ignore SMTP 550 invalid-recipient errors (default true) */
  forcesend?: boolean;
}

/** The id/seq/rev tuple returned by Create/Modify/Exception. */
export interface CalItemMutationResult {
  /** new/updated calendar item id */
  calItemId?: string;
  /** invite id */
  invId?: string;
  /** change-sequence (for follow-up conflict detection) */
  ms?: number;
  /** revision */
  rev?: number;
  /** echoed item, when `echo` was set */
  echo?: { m?: JsonObject };
  [k: string]: unknown;
}

/** Apply echo/forcesend options onto an outgoing request body. */
function applyMutationOptions(body: JsonObject, opts?: MutationOptions): void {
  if (!opts) return;
  if (opts.echo) body.echo = 1;
  if (opts.max !== undefined) body.max = opts.max;
  if (opts.html) body.html = 1;
  if (opts.neuter) body.neuter = 1;
  if (opts.forcesend !== undefined) body.forcesend = opts.forcesend ? 1 : 0;
}

/** Join a string|string[] of ids/names into the CSV the wire expects. */
function csv(v: string | string[] | undefined): string | undefined {
  if (v === undefined) return undefined;
  return Array.isArray(v) ? v.join(',') : v;
}

/* ========================================================================= */
/* Per-command param shapes                                                  */
/* ========================================================================= */

export interface GetAppointmentParams {
  /** appointment id (one of id|uid) */
  id?: string;
  /** iCalendar UID (one of id|uid) */
  uid?: string;
  /** also return modified date (`md`) */
  sync?: boolean;
  /** return `<mp>` body parts */
  includeContent?: boolean;
}

export interface CreateAppointmentParams extends MutationOptions {
  /** message envelope with the invite body */
  message: CalMessage;
}

export interface ModifyAppointmentParams extends MutationOptions {
  /** default invite id */
  id: string;
  /** component number */
  comp: number;
  /** change-sequence of the fetched version (conflict detection) */
  ms: number;
  /** revision of the fetched version */
  rev: number;
  /** message envelope with the new invite body */
  message: CalMessage;
}

export interface CancelAppointmentParams {
  /** default invite id */
  id: string;
  /** component number */
  comp: number;
  /** timezone def referenced by `instance` */
  tz?: CalTzInfo;
  /** cancel a single instance (omit to cancel the whole appointment) */
  instance?: DateTimeInfo;
  /** range when cancelling instances */
  range?: 'THISANDFUTURE' | 'THISANDPRIOR';
  /** optional cancellation-notice mail */
  notify?: CalMessage;
}

export interface SetAppointmentParams {
  /** target folder */
  folderId?: string;
  /** flags */
  flags?: string;
  /** tags */
  tags?: string;
  /** no next alarm (dismissed-alarm state) */
  noNextAlarm?: boolean;
  /** next alarm (ms epoch) */
  nextAlarm?: number;
  /** the default invite `<default ptst><m/></default>` */
  default: { ptst?: string; m: CalMessage; [k: string]: unknown };
  /** exception invites */
  except?: Array<{ ptst?: string; m: CalMessage; [k: string]: unknown }>;
  /** cancelled invites */
  cancel?: JsonObject[];
  /** saved replies (empty array/object clears; omit keeps existing) */
  replies?: JsonObject;
}

export interface CreateAppointmentExceptionParams extends MutationOptions {
  /** default invite id */
  id: string;
  comp: number;
  ms: number;
  rev: number;
  /**
   * RECURRENCE-ID — MUST match the SERIES rule instance start, not an already
   * moved exception time (edit those with modifyAppointment).
   */
  exceptId: DateTimeInfo;
  /** message envelope with the overriding invite body */
  message: CalMessage;
}

export interface AddAppointmentInviteParams {
  /** message envelope (same shape as SetAppointment `<default>`'s `<m>`) */
  message: CalMessage;
}

export interface SendInviteReplyParams {
  /** invite message id */
  id: string;
  /** component number */
  compNum: number;
  /** RSVP verb */
  verb: 'ACCEPT' | 'TENTATIVE' | 'DECLINE' | 'DELEGATED' | 'COMPLETED';
  /** reply to ONE instance only (its RECURRENCE-ID) */
  instanceExceptId?: DateTimeInfo;
  /** timezone def for `instanceExceptId` */
  tz?: CalTzInfo;
  /** update the organizer (default true; forced false if no/false RSVP) */
  updateOrganizer?: boolean;
  /** send-as identity id */
  identityId?: string;
  /** custom reply mail */
  message?: CalMessage;
}

export interface ExpandRecurParams {
  /** range start (ms epoch) */
  start: number;
  /** range end (ms epoch) */
  end: number;
  /** the series component (`<s>`,`<e>`|`<dur>`,`<recur>`) */
  comp: InviteComponent;
  /** exception components */
  exceptions?: InviteComponent[];
  /** cancellation `<exceptId>` markers */
  cancellations?: Array<{ exceptId: DateTimeInfo; [k: string]: unknown }>;
  /** timezone defs */
  tz?: CalTzInfo[];
}

export interface CheckRecurConflictsParams {
  /** range start (ms epoch) */
  start?: number;
  /** range end (ms epoch) */
  end?: number;
  /** return every instance, not just conflicting ones */
  all?: boolean;
  /** exclude this UID from conflict consideration */
  excludeUid?: string;
  /** the component to check */
  comp: InviteComponent;
  /** exception components */
  exceptions?: InviteComponent[];
  /** cancellation markers */
  cancellations?: Array<{ exceptId: DateTimeInfo; [k: string]: unknown }>;
  /** timezone defs */
  tz?: CalTzInfo[];
  /** users to check `<usr [id|name] [l]>` */
  attendees: Array<{ id?: string; name?: string; l?: string; [k: string]: unknown }>;
}

export interface CounterAppointmentParams {
  /** default invite id */
  id: string;
  comp: number;
  ms: number;
  rev: number;
  /** message + iCalendar COUNTER invite body */
  message: CalMessage;
}

export interface DeclineCounterAppointmentParams {
  /** message + iCalendar DECLINECOUNTER invite body */
  message: CalMessage;
}

export interface ForwardAppointmentParams {
  /** appointment item id */
  id: string;
  /** forward a single instance (its RECURRENCE-ID) */
  instanceExceptId?: DateTimeInfo;
  /** timezone def for `instanceExceptId` */
  tz?: CalTzInfo;
  /** forward mail (From, To+, Subject, body) */
  message: CalMessage;
}

export interface ForwardAppointmentInviteParams {
  /** invite message id */
  id: string;
  /** forward mail */
  message: CalMessage;
}

export interface GetFreeBusyParams {
  /** range start (ms epoch) */
  start: number;
  /** range end (ms epoch) */
  end: number;
  /** zimbraIds (csv) */
  ids?: string | string[];
  /** emails (csv) */
  names?: string | string[];
  /** exclude this appointment UID (reschedule conflict checks) */
  excludeUid?: string;
  /** per-folder lookups `<usr [id|name] [l]/>` */
  perFolder?: Array<{ id?: string; name?: string; l?: string; [k: string]: unknown }>;
}

export interface GetWorkingHoursParams {
  start: number;
  end: number;
  ids?: string | string[];
  names?: string | string[];
}

export interface RangeFolderParams {
  /** range start (ms epoch) */
  start: number;
  /** range end (ms epoch) */
  end: number;
  /** folder id */
  folderId?: string;
}

export interface GetAppointmentIdsSinceParams {
  /** last sync token (int) */
  lastSync: number;
  folderId?: string;
}

export interface GetMiniCalParams {
  start: number;
  end: number;
  /** folder ids (local and/or remote) */
  folderIds: string | string[];
  /** timezone id, or a full tz definition */
  tz?: string | CalTzInfo;
}

export interface DismissAlarmEntry {
  /** calendar item id */
  id: string;
  /** dismissed-at (ms epoch) */
  dismissedAt: number;
  /** target a task rather than an appointment */
  isTask?: boolean;
}

export interface SnoozeAlarmEntry {
  /** calendar item id */
  id: string;
  /** snooze until (ms epoch) */
  until: number;
  /** target a task rather than an appointment */
  isTask?: boolean;
}

export interface ImportAppointmentsParams {
  /** content type, e.g. "text/calendar" or "ics" */
  contentType: string;
  folderId?: string;
  /** uploaded attachment id (overrides inline) */
  attachId?: string;
  /** message id + part (alternative source) */
  messageId?: string;
  messagePart?: string;
  /** inline ICS content */
  content?: string;
}

export type GetICalParams =
  | { id: string }
  | { start: number; end: number; folderId?: string };

/* ========================================================================= */
/* Response shapes                                                           */
/* ========================================================================= */

export interface GetAppointmentResponse {
  appt?: Appointment[] | Appointment;
  [k: string]: unknown;
}

export interface GetRecurResponse {
  tz?: CalTzInfo[];
  comp?: InviteComponent[];
  except?: InviteComponent[];
  cancel?: InviteComponent[];
  [k: string]: unknown;
}

export interface ExpandRecurResponse {
  inst?: ExpandedInstance[];
  [k: string]: unknown;
}

export interface CheckRecurConflictsResponse {
  inst?: ConflictInstance[];
  [k: string]: unknown;
}

export interface GetFreeBusyResponse {
  usr?: FreeBusyUser[];
  [k: string]: unknown;
}

export interface GetWorkingHoursResponse {
  usr?: WorkingHoursUser[];
  [k: string]: unknown;
}

export interface AddAppointmentInviteResponse {
  /** omitted if the invite was ignored as outdated */
  calItemId?: string;
  invId?: string;
  compNum?: number;
  [k: string]: unknown;
}

export interface SendInviteReplyResponse {
  status?: 'OK' | 'OLD' | 'ALREADY-REPLIED' | 'FAIL';
  [k: string]: unknown;
}

export interface SetAppointmentResponse {
  calItemId?: string;
  default?: { id?: string; [k: string]: unknown };
  except?: Array<{ recurId?: string; id?: string; [k: string]: unknown }>;
  [k: string]: unknown;
}

export interface ImportAppointmentsResponse {
  appt?: { ids?: string; n?: number; [k: string]: unknown };
  [k: string]: unknown;
}

export interface GetMiniCalResponse {
  /** dates with at least one appointment (yyyymmdd) */
  dates: string[];
  /** per-folder access errors */
  errors: MiniCalError[];
}

/** Dismiss/Snooze response: `<appt|task calItemId><alarmData/></>+`. */
export interface AlarmStateResponse {
  appt?: JsonObject[];
  task?: JsonObject[];
  [k: string]: unknown;
}

/* ========================================================================= */
/* CalendarApi                                                               */
/* ========================================================================= */

/**
 * Calendar domain (urn:zimbraMail) — appointments, invites/RSVP, recurrence,
 * free/busy, working hours, alarms/reminders, mini-cal, summaries, ICS
 * import/export, and incremental sync.
 *
 * One typed method per SOAP command (see docs/features/calendar-tasks.md, the
 * authoritative feature→command map). The calendar LIST path itself is the mail
 * agent's `SearchRequest` (`types=appointment`, `calExpandInstStart/End`) — not
 * redefined here; `GetApptSummaries` is deprecated server-side and therefore has
 * no method (use `client.mail.search`). Task commands live in `TasksApi`.
 */
export class CalendarApi {
  constructor(private readonly client: ZimbraClient) {}

  /* ----------------------------------------------------------------------- */
  /* Appointments: get / create / modify / cancel / set / exception          */
  /* ----------------------------------------------------------------------- */

  /** GetAppointmentRequest — all invites of an appointment + attendee replies. */
  async getAppointment(params: GetAppointmentParams): Promise<GetAppointmentResponse> {
    const body: JsonObject = {};
    if (params.id !== undefined) body.id = params.id;
    if (params.uid !== undefined) body.uid = params.uid;
    if (params.sync) body.sync = 1;
    if (params.includeContent) body.includeContent = 1;
    return this.client.call<GetAppointmentResponse>(JSNS.mail, 'GetAppointment', body);
  }

  /** CreateAppointmentRequest — create an appointment and optionally email invites. */
  async createAppointment(params: CreateAppointmentParams): Promise<CalItemMutationResult> {
    const body: JsonObject = { m: params.message };
    applyMutationOptions(body, params);
    return this.client.call<CalItemMutationResult>(JSNS.mail, 'CreateAppointment', body);
  }

  /** ModifyAppointmentRequest — edit an appointment (or a series' default invites). */
  async modifyAppointment(params: ModifyAppointmentParams): Promise<CalItemMutationResult> {
    const body: JsonObject = {
      id: params.id,
      comp: params.comp,
      ms: params.ms,
      rev: params.rev,
      m: params.message,
    };
    applyMutationOptions(body, params);
    return this.client.call<CalItemMutationResult>(JSNS.mail, 'ModifyAppointment', body);
  }

  /** CancelAppointmentRequest — cancel the whole appointment or an instance/range. */
  async cancelAppointment(params: CancelAppointmentParams): Promise<JsonObject> {
    const body: JsonObject = { id: params.id, comp: params.comp };
    if (params.tz) body.tz = params.tz;
    if (params.instance) {
      const inst: JsonObject = { ...params.instance };
      if (params.range) inst.range = params.range;
      body.inst = inst;
    }
    if (params.notify) body.m = params.notify;
    return this.client.call(JSNS.mail, 'CancelAppointment', body);
  }

  /**
   * SetAppointmentRequest — directly set the full state (default + exceptions +
   * cancels + replies). Migration/import path, not normal edits.
   */
  async setAppointment(params: SetAppointmentParams): Promise<SetAppointmentResponse> {
    const body: JsonObject = { default: params.default };
    if (params.folderId !== undefined) body.l = params.folderId;
    if (params.flags !== undefined) body.f = params.flags;
    if (params.tags !== undefined) body.t = params.tags;
    if (params.noNextAlarm) body.noNextAlarm = 1;
    if (params.nextAlarm !== undefined) body.nextAlarm = params.nextAlarm;
    if (params.except) body.except = params.except;
    if (params.cancel) body.cancel = params.cancel;
    if (params.replies !== undefined) body.replies = params.replies;
    return this.client.call<SetAppointmentResponse>(JSNS.mail, 'SetAppointment', body);
  }

  /** CreateAppointmentExceptionRequest — override a single series instance. */
  async createAppointmentException(
    params: CreateAppointmentExceptionParams,
  ): Promise<CalItemMutationResult> {
    const inv = { ...(params.message.inv ?? {}), exceptId: params.exceptId };
    const body: JsonObject = {
      id: params.id,
      comp: params.comp,
      ms: params.ms,
      rev: params.rev,
      m: { ...params.message, inv },
    };
    applyMutationOptions(body, params);
    return this.client.call<CalItemMutationResult>(
      JSNS.mail,
      'CreateAppointmentException',
      body,
    );
  }

  /** AddAppointmentInviteRequest — add a VEVENT invite by UID (inbound mail). */
  async addAppointmentInvite(
    params: AddAppointmentInviteParams,
  ): Promise<AddAppointmentInviteResponse> {
    return this.client.call<AddAppointmentInviteResponse>(JSNS.mail, 'AddAppointmentInvite', {
      m: params.message,
    });
  }

  /* ----------------------------------------------------------------------- */
  /* RSVP & raw iCal reply                                                   */
  /* ----------------------------------------------------------------------- */

  /** SendInviteReplyRequest — Accept/Tentative/Decline (etc.) an invite. */
  async sendInviteReply(params: SendInviteReplyParams): Promise<SendInviteReplyResponse> {
    const body: JsonObject = { id: params.id, compNum: params.compNum, verb: params.verb };
    if (params.updateOrganizer !== undefined) {
      body.updateOrganizer = params.updateOrganizer ? 1 : 0;
    }
    if (params.identityId !== undefined) body.idnt = params.identityId;
    if (params.tz) body.tz = params.tz;
    if (params.instanceExceptId) body.exceptId = params.instanceExceptId;
    if (params.message) body.m = params.message;
    return this.client.call<SendInviteReplyResponse>(JSNS.mail, 'SendInviteReply', body);
  }

  /** ICalReplyRequest — process a raw iCalendar REPLY. */
  async iCalReply(icalText: string): Promise<JsonObject> {
    return this.client.call(JSNS.mail, 'ICalReply', { ical: { _content: icalText } });
  }

  /* ----------------------------------------------------------------------- */
  /* Recurrence: get / expand / conflict-check                               */
  /* ----------------------------------------------------------------------- */

  /** GetRecurRequest — the series recurrence definition. */
  async getRecur(id: string): Promise<GetRecurResponse> {
    return this.client.call<GetRecurResponse>(JSNS.mail, 'GetRecur', { id });
  }

  /** ExpandRecurRequest — expand a recurrence into instances over a range. */
  async expandRecur(params: ExpandRecurParams): Promise<ExpandRecurResponse> {
    const body: JsonObject = { s: params.start, e: params.end, comp: params.comp };
    if (params.tz) body.tz = params.tz;
    if (params.exceptions) body.except = params.exceptions;
    if (params.cancellations) body.cancel = params.cancellations;
    return this.client.call<ExpandRecurResponse>(JSNS.mail, 'ExpandRecur', body);
  }

  /** CheckRecurConflictsRequest — conflicting (or all) instances for attendees. */
  async checkRecurConflicts(
    params: CheckRecurConflictsParams,
  ): Promise<CheckRecurConflictsResponse> {
    const body: JsonObject = { comp: params.comp, usr: params.attendees };
    if (params.start !== undefined) body.s = params.start;
    if (params.end !== undefined) body.e = params.end;
    if (params.all) body.all = 1;
    if (params.excludeUid !== undefined) body.excludeUid = params.excludeUid;
    if (params.tz) body.tz = params.tz;
    if (params.exceptions) body.except = params.exceptions;
    if (params.cancellations) body.cancel = params.cancellations;
    return this.client.call<CheckRecurConflictsResponse>(
      JSNS.mail,
      'CheckRecurConflicts',
      body,
    );
  }

  /* ----------------------------------------------------------------------- */
  /* Counter / forward / organizer change                                    */
  /* ----------------------------------------------------------------------- */

  /** CounterAppointmentRequest — attendee proposes a new time/location. */
  async counterAppointment(params: CounterAppointmentParams): Promise<JsonObject> {
    return this.client.call(JSNS.mail, 'CounterAppointment', {
      id: params.id,
      comp: params.comp,
      ms: params.ms,
      rev: params.rev,
      m: params.message,
    });
  }

  /** DeclineCounterAppointmentRequest — organizer rejects a COUNTER. */
  async declineCounterAppointment(
    params: DeclineCounterAppointmentParams,
  ): Promise<JsonObject> {
    return this.client.call(JSNS.mail, 'DeclineCounterAppointment', { m: params.message });
  }

  /** ForwardAppointmentRequest — forward the actual appointment item. */
  async forwardAppointment(params: ForwardAppointmentParams): Promise<JsonObject> {
    const body: JsonObject = { id: params.id, m: params.message };
    if (params.tz) body.tz = params.tz;
    if (params.instanceExceptId) body.exceptId = params.instanceExceptId;
    return this.client.call(JSNS.mail, 'ForwardAppointment', body);
  }

  /** ForwardAppointmentInviteRequest — forward the invite mail only. */
  async forwardAppointmentInvite(
    params: ForwardAppointmentInviteParams,
  ): Promise<JsonObject> {
    return this.client.call(JSNS.mail, 'ForwardAppointmentInvite', {
      id: params.id,
      m: params.message,
    });
  }

  /** AnnounceOrganizerChangeRequest — notify attendees the organizer changed. */
  async announceOrganizerChange(id: string): Promise<JsonObject> {
    return this.client.call(JSNS.mail, 'AnnounceOrganizerChange', { id });
  }

  /* ----------------------------------------------------------------------- */
  /* Free/busy & working hours                                               */
  /* ----------------------------------------------------------------------- */

  /** GetFreeBusyRequest — free/busy intervals for one or more users. */
  async getFreeBusy(params: GetFreeBusyParams): Promise<GetFreeBusyResponse> {
    const body: JsonObject = { s: params.start, e: params.end };
    const ids = csv(params.ids);
    const names = csv(params.names);
    if (ids !== undefined) body.id = ids;
    if (names !== undefined) body.name = names;
    if (params.excludeUid !== undefined) body.excludeUid = params.excludeUid;
    if (params.perFolder) body.usr = params.perFolder;
    return this.client.call<GetFreeBusyResponse>(JSNS.mail, 'GetFreeBusy', body);
  }

  /** GetWorkingHoursRequest — configured working hours (free/busy-encoded). */
  async getWorkingHours(params: GetWorkingHoursParams): Promise<GetWorkingHoursResponse> {
    const body: JsonObject = { s: params.start, e: params.end };
    const ids = csv(params.ids);
    const names = csv(params.names);
    if (ids !== undefined) body.id = ids;
    if (names !== undefined) body.name = names;
    return this.client.call<GetWorkingHoursResponse>(JSNS.mail, 'GetWorkingHours', body);
  }

  /* ----------------------------------------------------------------------- */
  /* Summaries / sync / mini-cal                                             */
  /* ----------------------------------------------------------------------- */

  /**
   * GetCalendarItemSummariesRequest — both appts and tasks in one range call
   * (`LegacyCalendaringData`). Lightweight alternative to Search for folder loads.
   */
  async getCalendarItemSummaries(params: RangeFolderParams): Promise<JsonObject> {
    const body: JsonObject = { s: params.start, e: params.end };
    if (params.folderId !== undefined) body.l = params.folderId;
    return this.client.call(JSNS.mail, 'GetCalendarItemSummaries', body);
  }

  /** GetAppointmentIdsInRangeRequest — lightweight id+date list for a range. */
  async getAppointmentIdsInRange(
    params: RangeFolderParams,
  ): Promise<{ apptData?: AppointmentDatum[]; [k: string]: unknown }> {
    const body: JsonObject = { s: params.start, e: params.end };
    if (params.folderId !== undefined) body.l = params.folderId;
    return this.client.call<{ apptData?: AppointmentDatum[]; [k: string]: unknown }>(
      JSNS.mail,
      'GetAppointmentIdsInRange',
      body,
    );
  }

  /** GetAppointmentIdsSinceRequest — delta sync (modified + deleted ids). */
  async getAppointmentIdsSince(
    params: GetAppointmentIdsSinceParams,
  ): Promise<{ mids?: unknown; dids?: unknown; [k: string]: unknown }> {
    const body: JsonObject = { lastSync: params.lastSync };
    if (params.folderId !== undefined) body.l = params.folderId;
    return this.client.call<{ mids?: unknown; dids?: unknown; [k: string]: unknown }>(
      JSNS.mail,
      'GetAppointmentIdsSince',
      body,
    );
  }

  /**
   * GetMiniCalRequest — dates within a range (across folders) that have at
   * least one appointment. Normalizes `<date>` and `<error>` into arrays.
   */
  async getMiniCal(params: GetMiniCalParams): Promise<GetMiniCalResponse> {
    const ids = Array.isArray(params.folderIds) ? params.folderIds : [params.folderIds];
    const body: JsonObject = {
      s: params.start,
      e: params.end,
      folder: ids.map((id) => ({ id })),
    };
    if (params.tz !== undefined) {
      body.tz = typeof params.tz === 'string' ? { id: params.tz } : params.tz;
    }
    const raw = await this.client.call<{
      date?: Array<string | { _content?: string }>;
      error?: MiniCalError[];
    }>(JSNS.mail, 'GetMiniCal', body);
    const dates = (raw.date ?? []).map((d) =>
      typeof d === 'string' ? d : (d._content ?? ''),
    );
    return { dates, errors: raw.error ?? [] };
  }

  /* ----------------------------------------------------------------------- */
  /* Alarms / reminders                                                      */
  /* ----------------------------------------------------------------------- */

  /** DismissCalendarItemAlarmRequest — dismiss one or more alarms. */
  async dismissAlarm(entries: DismissAlarmEntry[]): Promise<AlarmStateResponse> {
    const body: JsonObject = {};
    for (const entry of entries) {
      const key = entry.isTask ? 'task' : 'appt';
      const node = { id: entry.id, dismissedAt: entry.dismissedAt };
      const existing = body[key];
      if (Array.isArray(existing)) existing.push(node);
      else if (existing) body[key] = [existing, node];
      else body[key] = node;
    }
    return this.client.call<AlarmStateResponse>(
      JSNS.mail,
      'DismissCalendarItemAlarm',
      body,
    );
  }

  /** SnoozeCalendarItemAlarmRequest — snooze one or more alarms until a time. */
  async snoozeAlarm(entries: SnoozeAlarmEntry[]): Promise<AlarmStateResponse> {
    const body: JsonObject = {};
    for (const entry of entries) {
      const key = entry.isTask ? 'task' : 'appt';
      const node = { id: entry.id, until: entry.until };
      const existing = body[key];
      if (Array.isArray(existing)) existing.push(node);
      else if (existing) body[key] = [existing, node];
      else body[key] = node;
    }
    return this.client.call<AlarmStateResponse>(JSNS.mail, 'SnoozeCalendarItemAlarm', body);
  }

  /** InvalidateReminderDeviceRequest — clear a reminder device email. */
  async invalidateReminderDevice(emailAddress: string): Promise<JsonObject> {
    return this.client.call(JSNS.mail, 'InvalidateReminderDevice', { a: emailAddress });
  }

  /** EnableSharedReminderRequest — toggle reminders for a mounted folder. */
  async enableSharedReminder(params: {
    mountpointId: string;
    reminder: boolean;
  }): Promise<JsonObject> {
    return this.client.call(JSNS.mail, 'EnableSharedReminder', {
      link: { id: params.mountpointId, reminder: params.reminder ? 1 : 0 },
    });
  }

  /* ----------------------------------------------------------------------- */
  /* ICS import / export                                                     */
  /* ----------------------------------------------------------------------- */

  /** ImportAppointmentsRequest — import ICS (inline, uploaded, or by message part). */
  async importAppointments(
    params: ImportAppointmentsParams,
  ): Promise<ImportAppointmentsResponse> {
    const body: JsonObject = { ct: params.contentType };
    if (params.folderId !== undefined) body.l = params.folderId;
    const content: JsonObject = {};
    if (params.attachId !== undefined) content.aid = params.attachId;
    if (params.messageId !== undefined) content.mid = params.messageId;
    if (params.messagePart !== undefined) content.part = params.messagePart;
    if (params.content !== undefined) content._content = params.content;
    body.content = content;
    return this.client.call<ImportAppointmentsResponse>(
      JSNS.mail,
      'ImportAppointments',
      body,
    );
  }

  /**
   * GetICalRequest — one invite's iCalendar (by `id`) or a whole range (`s`/`e`).
   * Returns the raw iCalendar text.
   */
  async getICal(params: GetICalParams): Promise<string> {
    const body: JsonObject = {};
    if ('id' in params) {
      body.id = params.id;
    } else {
      body.s = params.start;
      body.e = params.end;
      if (params.folderId !== undefined) body.l = params.folderId;
    }
    const raw = await this.client.call<{ ical?: string | { _content?: string } }>(
      JSNS.mail,
      'GetICal',
      body,
    );
    const ical = raw.ical;
    if (typeof ical === 'string') return ical;
    return ical?._content ?? '';
  }
}
